import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardActionArea,
    CardContent,
    IconButton,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CollectionsIcon from '@mui/icons-material/Collections';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useAlert } from '../../core/AlertContext';
import AuthenticatedImage from '../../core/AuthenticatedImage';
import {
    useDeleteMedia,
    useCalendarEntryMedia,
    useDownloadMediaZip,
    useRenameMedia,
    useUploadCalendarEntryMedia,
} from '../mediaApi';
import MediaGalleryDialog from './MediaGalleryDialog';
import { CalendarEntryMedia } from '../mediaSchemas';
import { fetchAuthenticatedBlobByUrl } from '../../hooks/useAuthenticatedImage';

interface MediaStripProps {
    entryId: number;
}

const parseBackendErrorMessage = (error: unknown): string => {
    if (!(error instanceof Error)) {
        return 'Aktion konnte nicht abgeschlossen werden.';
    }

    const marker = ' - ';
    const markerIndex = error.message.indexOf(marker);
    if (markerIndex === -1) {
        return error.message;
    }

    const payload = error.message.slice(markerIndex + marker.length);
    try {
        const parsed = JSON.parse(payload) as Record<string, unknown>;
        const details = Object.entries(parsed)
            .map(([key, value]) => {
                if (Array.isArray(value) && value.length > 0) {
                    return `${key}: ${String(value[0])}`;
                }
                if (typeof value === 'string') {
                    return `${key}: ${value}`;
                }
                return null;
            })
            .filter((value): value is string => Boolean(value));

        if (details.length > 0) {
            return details.join(' | ');
        }
    } catch (_parseError) {
        return payload;
    }

    return error.message;
};

const getStatusCodeFromError = (error: unknown): number | null => {
    if (!(error instanceof Error)) {
        return null;
    }

    const match = error.message.match(/Error:\s(\d{3})/);
    if (!match) {
        return null;
    }

    const parsed = Number(match[1]);
    return Number.isNaN(parsed) ? null : parsed;
};

const getDisplayTitle = (item: CalendarEntryMedia): string => item.title?.trim() || item.original_filename;

const MediaStrip: React.FC<MediaStripProps> = ({ entryId }) => {
    const { showAlert } = useAlert();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [galleryOpen, setGalleryOpen] = useState<boolean>(false);
    const [isZipEndpointUnavailable, setZipEndpointUnavailable] = useState<boolean>(false);

    const { media, isLoadingMedia, isErrorMedia, mediaError } = useCalendarEntryMedia(entryId);
    const uploadMediaMutation = useUploadCalendarEntryMedia(entryId);
    const deleteMediaMutation = useDeleteMedia();
    const renameMediaMutation = useRenameMedia();
    const downloadZipMutation = useDownloadMediaZip();

    const currentUserId = useMemo(() => {
        const raw = localStorage.getItem('user_id');
        if (!raw) {
            return null;
        }

        const parsed = Number(raw);
        return Number.isNaN(parsed) ? null : parsed;
    }, []);

    useEffect(() => {
        setZipEndpointUnavailable(false);
    }, [entryId]);

    const handlePickFiles = (): void => {
        if (uploadMediaMutation.isPending) {
            return;
        }
        fileInputRef.current?.click();
    };

    const handleUploadFiles = async (selectedFiles: File[]): Promise<void> => {
        if (selectedFiles.length === 0) {
            return;
        }

        try {
            await uploadMediaMutation.mutateAsync(selectedFiles);
            showAlert({ severity: 'success', text: `${selectedFiles.length} Datei(en) hochgeladen.` });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
        }
    };

    const handleFileChange = async ({ target }: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const selectedFiles = target.files ? Array.from(target.files) : [];
        await handleUploadFiles(selectedFiles);
        target.value = '';
    };

    const handleDeleteMedia = async (mediaId: number): Promise<void> => {
        try {
            await deleteMediaMutation.mutateAsync({
                mediaId,
                entryId,
            });
            showAlert({ severity: 'success', text: 'Medium gelöscht.' });
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
            throw error;
        }
    };

    const handleRenameMedia = async (mediaId: number, title: string): Promise<void> => {
        try {
            await renameMediaMutation.mutateAsync({
                mediaId,
                entryId,
                title,
            });
            showAlert({ severity: 'success', text: 'Medium umbenannt.' });
        } catch (error) {
            const statusCode = getStatusCodeFromError(error);
            if (statusCode === 405) {
                showAlert({ severity: 'error', text: 'Umbenennen ist serverseitig noch nicht aktiviert (405).' });
                throw error;
            }
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
            throw error;
        }
    };

    const handleDownloadMedia = async (item: CalendarEntryMedia): Promise<void> => {
        try {
            const blob = await fetchAuthenticatedBlobByUrl(item.url);
            const objectUrl = URL.createObjectURL(blob);
            const fileName = getDisplayTitle(item).trim() || `media-${item.id}`;
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
        }
    };

    const handleDownloadZip = async (mediaIds: number[]): Promise<void> => {
        try {
            const zipBlob = await downloadZipMutation.mutateAsync({
                entryId,
                mediaIds,
            });
            const objectUrl = URL.createObjectURL(zipBlob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = `calendar-entry-${entryId}-photos.zip`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            const statusCode = getStatusCodeFromError(error);
            if (statusCode === 404) {
                setZipEndpointUnavailable(true);
                showAlert({ severity: 'error', text: 'ZIP Download Endpoint fehlt (404).' });
                return;
            }
            showAlert({ severity: 'error', text: parseBackendErrorMessage(error) });
        }
    };

    return (
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Fotos
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Alle Fotos">
                            <span>
                                <IconButton onClick={() => setGalleryOpen(true)} disabled={media.length === 0}>
                                    <CollectionsIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Hochladen">
                            <span>
                                <IconButton onClick={handlePickFiles} disabled={uploadMediaMutation.isPending}>
                                    <AddPhotoAlternateIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    hidden
                    onChange={(event) => void handleFileChange(event)}
                />

                {isErrorMedia && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        {parseBackendErrorMessage(mediaError)}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        pb: 0.5,
                        minHeight: 132,
                        '&::-webkit-scrollbar': { height: 8 },
                    }}
                >
                    {isLoadingMedia && (
                        <>
                            {[0, 1, 2].map((item) => (
                                <Card key={item} variant="outlined" sx={{ flex: '0 0 auto', width: { xs: 140, sm: 180 } }}>
                                    <Skeleton variant="rectangular" width="100%" height={120} />
                                    <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                                        <Skeleton variant="text" width="70%" />
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    )}

                    {!isLoadingMedia && media.length === 0 && (
                        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', minHeight: 120 }}>
                            <Typography variant="body2" color="text.secondary">
                                Noch keine Medien hochgeladen.
                            </Typography>
                        </Stack>
                    )}

                    {!isLoadingMedia && media.map((item) => (
                        <Card key={item.id} variant="outlined" sx={{ flex: '0 0 auto', width: { xs: 140, sm: 180 } }}>
                            <CardActionArea onClick={() => setGalleryOpen(true)}>
                                {item.type === 'image' ? (
                                    <AuthenticatedImage
                                        url={item.url}
                                        alt={getDisplayTitle(item)}
                                        sx={{ width: '100%', height: { xs: 104, sm: 120 }, objectFit: 'cover' }}
                                    />
                                ) : (
                                    <Stack
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            height: { xs: 104, sm: 120 },
                                            bgcolor: 'action.hover',
                                        }}
                                    >
                                        <InsertDriveFileIcon color="action" />
                                    </Stack>
                                )}
                            </CardActionArea>
                            <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
                                    <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
                                        {getDisplayTitle(item)}
                                    </Typography>
                                    <IconButton size="small" onClick={() => void handleDownloadMedia(item)}>
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                <MediaGalleryDialog
                    open={galleryOpen}
                    media={media}
                    currentUserId={currentUserId}
                    isDeleting={deleteMediaMutation.isPending}
                    isRenaming={renameMediaMutation.isPending}
                    onClose={() => setGalleryOpen(false)}
                    onDelete={handleDeleteMedia}
                    onRename={handleRenameMedia}
                    onDownload={handleDownloadMedia}
                    onDownloadZip={handleDownloadZip}
                    isDownloadingZip={downloadZipMutation.isPending}
                    isUploading={uploadMediaMutation.isPending}
                    onUpload={handleUploadFiles}
                    isZipUnavailable={isZipEndpointUnavailable}
                />
            </CardContent>
        </Card>
    );
};

export default MediaStrip;
